import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  ApiError,
  apiFetch,
  getToken,
  setSessionInvalidHandler,
  setToken,
} from "@/lib/api";
import {
  biometricLoginRequest,
  isWebauthnCancel,
} from "@/lib/webauthn";
import type { User } from "@/types";

type AuthResult = {
  error?: string;
};

type AuthContextValue = {
  user: User | null;
  /** true enquanto a sessão persistida ainda está sendo validada no boot */
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthResult>;
  /** Login por biometria (WebAuthn); cancelamento do prompt = {} sem erro. */
  loginWithBiometrics: () => Promise<AuthResult>;
  register: (
    name: string,
    email: string,
    password: string,
    nickname?: string,
  ) => Promise<AuthResult>;
  /**
   * Confirma o e-mail pelo token do link e JÁ abre sessão (a resposta tem o
   * mesmo shape do login). `expired` sinaliza "ofereça reenviar o link".
   */
  verifyEmail: (token: string) => Promise<AuthResult & { expired?: boolean }>;
  resendVerification: (email: string) => Promise<AuthResult>;
  forgotPassword: (email: string) => Promise<AuthResult>;
  resetPassword: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<AuthResult>;
  logout: () => void;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function toAuthResult(err: unknown): AuthResult {
  if (err instanceof ApiError) {
    return { error: err.message };
  }
  return { error: "Não foi possível conectar ao servidor." };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  // 401 em qualquer request autenticado derruba a sessão centralmente.
  useEffect(() => {
    setSessionInvalidHandler(logout);
    return () => setSessionInvalidHandler(null);
  }, [logout]);

  // Boot: valida o token persistido contra o servidor (nunca confia no
  // localStorage sozinho — mesma regra do app original).
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    apiFetch<{ user: User }>("/api/auth/me")
      .then(({ user }) => setUser(user))
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      try {
        const data = await apiFetch<{ token: string; user: User }>(
          "/api/auth/login",
          {
            method: "POST",
            auth: false,
            body: JSON.stringify({ email, password }),
          },
        );
        setToken(data.token);
        setUser(data.user);
        return {};
      } catch (err) {
        return toAuthResult(err);
      }
    },
    [],
  );

  const loginWithBiometrics = useCallback(async (): Promise<AuthResult> => {
    try {
      const data = await biometricLoginRequest();
      setToken(data.token);
      setUser(data.user);
      return {};
    } catch (err) {
      if (isWebauthnCancel(err)) return {};
      return toAuthResult(err);
    }
  }, []);

  // Cadastro: o registro responde 201 sem token (resposta idêntica quando o
  // e-mail já existe — anti-enumeração), então o login é feito em seguida com
  // as mesmas credenciais. Se o e-mail já tinha conta com outra senha, o login
  // falha e o dono do endereço recebe por e-mail a orientação "você já tem
  // conta".
  //
  // A confirmação de e-mail vai por LINK no e-mail de boas-vindas e NÃO
  // bloqueia nada: clicar apenas marca `email_verified` e abre sessão direto
  // na home (ver pages/VerificarEmail.tsx).
  const register = useCallback(
    async (
      name: string,
      email: string,
      password: string,
      nickname?: string,
    ): Promise<AuthResult> => {
      try {
        await apiFetch("/api/auth/register", {
          method: "POST",
          auth: false,
          body: JSON.stringify({ name, email, password, nickname }),
        });
      } catch (err) {
        return toAuthResult(err);
      }
      return login(email, password);
    },
    [login],
  );

  const verifyEmail = useCallback(
    async (token: string): Promise<AuthResult & { expired?: boolean }> => {
      try {
        const data = await apiFetch<{ token: string; user: User }>(
          "/api/auth/verify-email",
          {
            method: "POST",
            auth: false,
            body: JSON.stringify({ token }),
          },
        );
        setToken(data.token);
        setUser(data.user);
        return {};
      } catch (err) {
        const expired =
          err instanceof ApiError &&
          typeof err.data === "object" &&
          err.data !== null &&
          (err.data as { expired?: boolean }).expired === true;
        return { ...toAuthResult(err), ...(expired ? { expired } : {}) };
      }
    },
    [],
  );

  const resendVerification = useCallback(
    async (email: string): Promise<AuthResult> => {
      try {
        await apiFetch("/api/auth/resend-verification", {
          method: "POST",
          auth: false,
          body: JSON.stringify({ email }),
        });
        return {};
      } catch (err) {
        return toAuthResult(err);
      }
    },
    [],
  );

  const forgotPassword = useCallback(
    async (email: string): Promise<AuthResult> => {
      try {
        await apiFetch("/api/auth/forgot-password", {
          method: "POST",
          auth: false,
          body: JSON.stringify({ email }),
        });
        return {};
      } catch (err) {
        return toAuthResult(err);
      }
    },
    [],
  );

  const resetPassword = useCallback(
    async (
      email: string,
      code: string,
      newPassword: string,
    ): Promise<AuthResult> => {
      try {
        await apiFetch("/api/auth/reset-password", {
          method: "POST",
          auth: false,
          body: JSON.stringify({ email, code, newPassword }),
        });
        return {};
      } catch (err) {
        return toAuthResult(err);
      }
    },
    [],
  );

  const refreshUser = useCallback(async () => {
    if (!getToken()) return;
    try {
      const { user } = await apiFetch<{ user: User }>("/api/auth/me");
      setUser(user);
    } catch {
      // sessão inválida já tratada pelo handler central
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithBiometrics,
        register,
        verifyEmail,
        resendVerification,
        forgotPassword,
        resetPassword,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de <AuthProvider>");
  return ctx;
}
